import { useEffect, useRef, useCallback } from "react";
import { pushDataLayer } from "./GoogleTagManager";

const storageKeys = {
	sessionStart: "quality_visit_session_start",
	timeSpentTriggered: "quality_visit_time_spent_triggered",
	scrollTriggered: "quality_visit_scroll_triggered",
	multiPageTriggered: "quality_visit_multi_page_triggered",
	pageCount: "quality_visit_page_count",
} as const;

const oneMinuteMs = 60 * 1000;
const scrollThreshold = 0.5;

/**
 * Hook using sessionStorage to track "quality visit" KPIs, trigger events and push to dataLayer:
 *
 * 1. Time spent: 1+ minute on the website (across page navigations)
 * 2. Scroll: 50% scroll depth on any page
 * 3. Multi-page: 2+ pages visited in a session
 *
 * Each KPI fires only once per session via sessionStorage.
 *
 * @param routeKey - A unique identifier for the current route (e.g. route.itemId from Sitecore context)
 *                   Used to detect page navigations for multi-page tracking
 */
export function useQualityVisitTracking(routeKey?: string) {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const scrollListenerRef = useRef<(() => void) | null>(null);

	const isBrowser = typeof window !== "undefined" && typeof sessionStorage !== "undefined";

	// Push quality visit event to dataLayer
	const pushQualityVisit = useCallback((triggerType: "time_spent" | "scroll" | "multi_page") => {
		pushDataLayer(() => ({
			event: "quality_visit_fragment",
			trigger_type: triggerType,
		}));
	}, []);


	// 1. TIME SPENT TRACKING
	const initTimeSpentTracking = useCallback(() => {
		if (!isBrowser) return;

		// Check if already triggered this session
		if (sessionStorage.getItem(storageKeys.timeSpentTriggered)) {
			return;
		}

		// Get or set session start time
		let sessionStartTime = sessionStorage.getItem(storageKeys.sessionStart);
		if (!sessionStartTime) {
			sessionStartTime = Date.now().toString();
			sessionStorage.setItem(storageKeys.sessionStart, sessionStartTime);
		}

		const startTime = parseInt(sessionStartTime, 10);
		const timeElapsed = Date.now() - startTime;
		const timeRemaining = oneMinuteMs - timeElapsed;

		// If already past 1 minute, trigger immediately
		if (timeRemaining <= 0) {
			pushQualityVisit("time_spent");
			sessionStorage.setItem(storageKeys.timeSpentTriggered, "true");
			return;
		}

		// Clear existing timer
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		// Set timer for remaining time
		timerRef.current = setTimeout(() => {
			// Race condition safety check
			if (!sessionStorage.getItem(storageKeys.timeSpentTriggered)) {
				pushQualityVisit("time_spent");
				sessionStorage.setItem(storageKeys.timeSpentTriggered, "true");
			}
		}, timeRemaining);
	}, [isBrowser, pushQualityVisit]);

	// 2. SCROLL TRACKING
	const initScrollTracking = useCallback(() => {
		if (!isBrowser) return;

		// Check if already triggered this session
		if (sessionStorage.getItem(storageKeys.scrollTriggered)) {
			return;
		}

		// Remove any existing listener
		if (scrollListenerRef.current) {
			window.removeEventListener("scroll", scrollListenerRef.current);
		}

		const handleScroll = () => {
			// Check again in case it was triggered elsewhere
			if (sessionStorage.getItem(storageKeys.scrollTriggered)) {
				if (scrollListenerRef.current) {
					window.removeEventListener("scroll", scrollListenerRef.current);
				}
				return;
			}

			const scrollTop = window.scrollY || document.documentElement.scrollTop;
			const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;

			// Avoid division by zero on pages with no scroll
			if (scrollHeight <= 0) return;

			const scrollPercentage = scrollTop / scrollHeight;

			if (scrollPercentage >= scrollThreshold) {
				pushQualityVisit("scroll");
				sessionStorage.setItem(storageKeys.scrollTriggered, "true");

				// Clean up listener after triggering
				if (scrollListenerRef.current) {
					window.removeEventListener("scroll", scrollListenerRef.current);
				}
			}
		};

		scrollListenerRef.current = handleScroll;
		window.addEventListener("scroll", handleScroll, { passive: true });

		// Check initial scroll position
		handleScroll();
	}, [isBrowser, pushQualityVisit]);

	// 3. MULTI-PAGE TRACKING
	const initMultiPageTracking = useCallback(() => {
		if (!isBrowser) return;

		// Check if already triggered this session
		if (sessionStorage.getItem(storageKeys.multiPageTriggered)) {
			return;
		}

		// Get current page count and increment
		const currentCount = parseInt(sessionStorage.getItem(storageKeys.pageCount) || "0", 10);
		const newCount = currentCount + 1;
		sessionStorage.setItem(storageKeys.pageCount, newCount.toString());

		// Trigger on 2+ pages
		if (newCount >= 2) {
			pushQualityVisit("multi_page");
			sessionStorage.setItem(storageKeys.multiPageTriggered, "true");
		}
	}, [isBrowser, pushQualityVisit]);

	// Initialize all tracking events
	// Track previous route to detect actual navigation
	const prevRouteKeyRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!isBrowser) return;

		const isNewPageView = prevRouteKeyRef.current !== routeKey;

		// Initialize time spent tracking (on every page view to restart timer with remaining time)
		initTimeSpentTracking();

		// Initialize scroll tracking (on every page view since content changes)
		initScrollTracking();

		// Track multi-page visits (increment count on each new page view)
		if (isNewPageView) {
			initMultiPageTracking();
			prevRouteKeyRef.current = routeKey;
		}

		// Cleanup
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
			if (scrollListenerRef.current) {
				window.removeEventListener("scroll", scrollListenerRef.current);
			}
		};
	}, [isBrowser, routeKey, initTimeSpentTracking, initScrollTracking, initMultiPageTracking]);
}

